import {Component, OnInit} from "@angular/core";
import {FormGroup, FormControl, Validators} from "@angular/forms";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {ExamService} from "../../../shared/services/exam/exam.service";
import {take} from "rxjs/operators";
import {Exam} from "../../../shared/models/exam";
import {InvoiceService} from "../../../shared/services/invoice/invoice.service";
import {Invoice, InvoiceVoucherRequest} from "../../../shared/models/invoice";
import {forkJoin} from "rxjs";
import {ExamCenterService} from "../../../shared/services/exam-center/exam-center.service";
import {ExamCenter} from "../../../shared/models/examCenter";
import {ExamSessionService} from "../../../shared/services/exam-session/exam-session.service";
import {ShortExamSession} from "../../../shared/models/examSession";
import {CertificationService} from "../../../shared/services/certification/certification.service";
import {Certification} from "../../../shared/models/certification";

@Component({
    selector: "app-exam-modal",
    templateUrl: "./exam-modal.component.html",
    styleUrls: ["./exam-modal.component.scss"]
})
export class ExamModalComponent implements OnInit {
  examForm: FormGroup;
  invoices: Array<Invoice>;
  examCenters: Array<ExamCenter>;
  certifications: Array<Certification>;

  // Sessions proposées pour la pré-assignation : uniquement celles du centre
  // sélectionné et à venir. Un voucher ne peut être rattaché qu'à une session
  // de son propre centre d'examen.
  sessions: Array<ShortExamSession> = [];
  isSessionsLoading: boolean = false;

  isDataLoading: boolean = true;
  error: string;
  submitting: boolean = false;

  constructor (
    public activeModal: NgbActiveModal,
    public invoiceService: InvoiceService,
    public examCenterService: ExamCenterService,
    public examSessionService: ExamSessionService,
    public certificationService: CertificationService,
    public examService: ExamService
  ) {
      this.examForm = new FormGroup({
          count: new FormControl(1),
          invoice: new FormControl(
              "",
              Validators.required
          ),
          examCenter: new FormControl(
              "",
              Validators.required
          ),

          // Pré-assignation facultative, appliquée à tous les codes générés.
          certification: new FormControl(""),
          session: new FormControl("")
      });
      this.isDataLoading = true;
  }

  ngOnInit () {
      const getExamCenters = this.examCenterService.list(true, true);
      const getInvoices = this.invoiceService.list(true);
      const getCertifications = this.certificationService.shortListUnarchived();

      // Fill user form once all data has been fetched
      forkJoin(
          getExamCenters,
          getInvoices,
          getCertifications
      ).pipe(take(1)).
          subscribe(([
              examCenters,
              invoices,
              certifications
          ]) => {
              this.examCenters = examCenters;
              this.invoices = invoices;
              this.certifications = certifications;
              this.isDataLoading = false;
          });

      // Les sessions dépendent du centre : on les recharge à chaque changement
      // et on invalide la session déjà choisie, qui appartiendrait à un autre
      // centre.
      this.examForm.get("examCenter").valueChanges.subscribe((examCenterId: string) => {
          this.examForm.get("session").setValue("");
          this.loadSessions(examCenterId);
      });

      // La facture porte la demande de tickets à laquelle elle répond : on
      // propose la quantité demandée dès qu'elle est choisie.
      this.examForm.get("invoice").valueChanges.subscribe((invoiceId: string) => {
          this.prefillCountFromInvoice(invoiceId);
      });
  }

  /* La demande de tickets à laquelle répond la facture choisie, s'il y en a une. */
  get selectedInvoiceRequest (): InvoiceVoucherRequest {
      return this.invoiceRequest(this.examForm.value.invoice);
  }

  private invoiceRequest (invoiceId: any): InvoiceVoucherRequest {
      const invoice = this.invoices?.find((i: Invoice) => i.id == invoiceId);

      return invoice?.voucher_requests?.length
          ? invoice.voucher_requests[0]
          : null;
  }

  /*
   * Simple proposition : une saisie manuelle du staff prime, le nombre généré
   * reste libre (il peut différer de la quantité demandée).
   *
   * L'identifiant vient de `valueChanges` : un contrôle émet avant que le
   * groupe parent ait recalculé sa valeur, `examForm.value` porterait encore
   * la facture précédente.
   */
  private prefillCountFromInvoice (invoiceId: string) {
      const countControl = this.examForm.get("count");
      const request = this.invoiceRequest(invoiceId);

      if (countControl.dirty || !request) {
          return;
      }

      countControl.setValue(request.quantity);
  }

  private loadSessions (examCenterId: string) {
      if (!examCenterId) {
          this.sessions = [];
          return;
      }

      this.isSessionsLoading = true;
      this.examSessionService.shortList(
          examCenterId,
          true
      ).pipe(take(1)).
          subscribe(
              (sessions: Array<ShortExamSession>) => {
                  this.sessions = sessions;
                  this.isSessionsLoading = false;
              },
              () => {
                  this.sessions = [];
                  this.isSessionsLoading = false;
              }
          );
  }

  validate () {
      this.submitting = true;

      const {count} = this.examForm.value;
      const invoice = this.invoices.find((i: Invoice) => i.id == this.examForm.value.invoice);
      const examCenter = this.examCenters.find((e: ExamCenter) => e.id == this.examForm.value.examCenter);
      const sessionId = this.examForm.value.session
          ? Number(this.examForm.value.session)
          : null;
      const certificationId = this.examForm.value.certification
          ? Number(this.examForm.value.certification)
          : null;

      this.examService.generate(
          count,
          invoice,
          examCenter,
          sessionId,
          certificationId
      ).pipe(take(1)).
          subscribe(
              (res: Array<Exam>) => {
                  this.activeModal.close(res);
              },
              (err: any) => {
                  this.error = "Une erreur est survenue lors de la création des codes";
                  this.submitting = false;
              }
          );
  }

}
